import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { NOTIFICATION_CENTER_TAB_KEYS } from "./notificationCenterConstants";
import {
  getNotificationCenter,
  getNotificationCenterCounters,
} from "./notificationCenterApi";

function normalizeList(value) {
  if (Array.isArray(value)) return value;
  if (value?.items && Array.isArray(value.items)) return value.items;
  if (value?.data && Array.isArray(value.data)) return value.data;
  if (value?.results && Array.isArray(value.results)) return value.results;
  return [];
}

function createEmptyByTab() {
  return Object.fromEntries(
    NOTIFICATION_CENTER_TAB_KEYS.map((tabId) => [tabId, { default: [] }])
  );
}

function createEmptyByTableCounts() {
  return Object.fromEntries(
    NOTIFICATION_CENTER_TAB_KEYS.map((tabId) => [tabId, 0])
  );
}

/**
 * API shape: { total: number, by_table: { reporting?: number, ... } }
 */
export function normalizeNotificationCenterCounters(raw) {
  const byTable = createEmptyByTableCounts();

  if (!raw || typeof raw !== "object") {
    return { total: 0, byTable };
  }

  const source =
    raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)
      ? raw.data
      : raw;

  const incoming = source.by_table ?? source.byTable ?? {};
  for (const tabId of NOTIFICATION_CENTER_TAB_KEYS) {
    if (incoming[tabId] != null) {
      byTable[tabId] = Number(incoming[tabId]) || 0;
    }
  }

  return {
    total: Number(source.total) || 0,
    byTable,
  };
}

/**
 * API shape: { reporting?: Row[], messages?: Row[], cases?: Row[], ... }
 * Each known tab key maps to an array; UI reads dataByTab[tabId].default.
 */
export function normalizeNotificationCenterData(raw) {
  const byTab = createEmptyByTab();

  if (!raw || typeof raw !== "object") {
    return byTab;
  }

  const source =
    raw.data &&
    typeof raw.data === "object" &&
    !Array.isArray(raw.data)
      ? raw.data
      : raw;

  for (const tabId of NOTIFICATION_CENTER_TAB_KEYS) {
    const node = source[tabId];
    if (node === undefined) {
      continue;
    }

    if (Array.isArray(node)) {
      byTab[tabId] = { default: normalizeList(node) };
      continue;
    }

    if (node && typeof node === "object") {
      const directList = normalizeList(node);
      if (directList.length > 0) {
        byTab[tabId] = { default: directList };
        continue;
      }

      const sections = { default: [] };
      for (const [sectionKey, sectionValue] of Object.entries(node)) {
        sections[sectionKey] = normalizeList(sectionValue);
      }
      byTab[tabId] = sections;
    }
  }

  return byTab;
}

function isSilentFetch(arg) {
  return Boolean(arg && typeof arg === "object" && arg.silent);
}

export const fetchNotificationCenter = createAsyncThunk(
  "notificationCenter/fetch",
  async (options = {}, { rejectWithValue }) => {
    try {
      const response = await getNotificationCenter();
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || err.error || "Server error");
      }
      const data = await response.json();
      return normalizeNotificationCenterData(data);
    } catch (e) {
      return rejectWithValue(e?.message || "Failed to load notification center");
    }
  },
);

export const fetchNotificationCenterCounters = createAsyncThunk(
  "notificationCenter/fetchCounters",
  async (options = {}, { rejectWithValue }) => {
    try {
      const response = await getNotificationCenterCounters();
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || err.error || "Server error");
      }
      const data = await response.json();
      return normalizeNotificationCenterCounters(data);
    } catch (e) {
      return rejectWithValue(
        e?.message || "Failed to load notification center counters"
      );
    }
  },
);

/** Reload table data and bell/summary counters after read, close, etc. */
export const refreshNotificationCenterAfterAction = createAsyncThunk(
  "notificationCenter/refreshAfterAction",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await Promise.all([
        dispatch(fetchNotificationCenter({ silent: true })).unwrap(),
        dispatch(fetchNotificationCenterCounters({ silent: true })).unwrap(),
      ]);
    } catch (e) {
      return rejectWithValue(
        e?.message || "Failed to refresh notification center"
      );
    }
  },
);

const initialState = {
  dataByTab: createEmptyByTab(),
  loading: false,
  error: null,
  counters: normalizeNotificationCenterCounters(null),
  countersLoading: false,
  countersError: null,
};

const notificationCenterSlice = createSlice({
  name: "notificationCenter",
  initialState,
  reducers: {
    clearNotificationCenterError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotificationCenter.pending, (state, action) => {
        if (!isSilentFetch(action.meta?.arg)) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchNotificationCenter.fulfilled, (state, action) => {
        state.loading = false;
        state.dataByTab = action.payload ?? createEmptyByTab();
      })
      .addCase(fetchNotificationCenter.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || "Failed to load";
      })
      .addCase(fetchNotificationCenterCounters.pending, (state, action) => {
        if (!isSilentFetch(action.meta?.arg)) {
          state.countersLoading = true;
        }
        state.countersError = null;
      })
      .addCase(fetchNotificationCenterCounters.fulfilled, (state, action) => {
        state.countersLoading = false;
        state.counters = action.payload ?? normalizeNotificationCenterCounters(null);
      })
      .addCase(fetchNotificationCenterCounters.rejected, (state, action) => {
        state.countersLoading = false;
        state.countersError =
          action.payload || action.error?.message || "Failed to load counters";
      });
  },
});

export const { clearNotificationCenterError } = notificationCenterSlice.actions;
export default notificationCenterSlice.reducer;
